<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Rejected Claim - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            tag="form-button"
            :indicator="indicator"
            @clicked="openClaim"
            v-if="!readonly"
          >
            Re-Open Claim
          </page-header-action>
        </template>
      </page-header>

      <div class="alert alert-info text-center">
        <small style="color: #fff;">Rejected Reason</small><br />
        {{ rejectionReason?.body }}
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div class="card mb-3">
            <incident-tabs
              :source="source"
              :tabs="tabs"
              :incident="incident"
              :module="module"
              v-on:selected="setActive"
              :active="currentView"
              :email="false"
            ></incident-tabs>

            <transition name="component-fade" mode="out-in">
              <incident-source
                :incident="incident"
                :showCancel="showCancel"
                :readonly="readonly"
                v-on:created="activateTabs"
                v-on:exists="activateTabs"
                v-if="currentView == 'source'"
                v-on:unsaved-data="saveStatus"
              ></incident-source>

              <accident-details
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'accident_description'"
                v-on:unsaved-data="saveStatus"
              ></accident-details>

              <client
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'client'"
                v-on:unsaved-data="saveStatus"
              ></client>

              <third-parties
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'third_parties'"
                v-on:unsaved-data="saveStatus"
                key="third_parties"
              ></third-parties>

              <witnesses
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'witnesses'"
              ></witnesses>

              <statements
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'statements'"
                v-on:unsaved-data="saveStatus"
              ></statements>

              <documents
                :incident="incident"
                :rejected="true"
                :readonly="readonly"
                v-if="currentView == 'documents'"
              ></documents>

              <jobs
                :incident="source.incident_id"
                v-if="currentView == 'jobs'"
                key="jobs"
              ></jobs>

              <email :incident="incident" v-if="currentView == 'email'"></email>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      :rejected="true"
      :create="false"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import Source from '../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from '../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from '../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from '../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from '../FirstNoticeOfLoss/Sections/Witnesses.vue';
import Statements from '../FirstNoticeOfLoss/Sections/Statements.vue';
import Notes from './../Shared/Notes.vue';
import IncidentTabs from '@/components/Modules/Shared/IncidentTabs';
import Documents from '@/components/Modules/Shared/Documents';
import Email from '@/components/Modules/Shared/Email';
import Jobs from './../FirstNoticeOfLoss/Sections/Jobs';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    PageHeader,
    PageHeaderAction,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    'notes-window': Notes,
    IncidentTabs,
    Documents,
    Email,
    Jobs,
    Statements
  },

  data() {
    return {
      module: 'Rejected',
      loadingIncident: true,
      notes: false,
      indicator: false,
      currentView: 'source',
      unsaved_data: false,
      tabs: [
        {
          name: 'jobs',
          label: 'Jobs',
          disabled: false
        }
      ],
      showCancel: false
    };
  },
  computed: {
    readonly() {
      return this.isReadonly();
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];
      if (source && source.claim_reference) {
        return source.claim_reference;
      }
      return null;
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    rejectionReason() {
      return this.$store.getters['Incident/rejectionReason'];
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },
  mounted() {
    this.retrieveAndSetIncident();
  },

  methods: {
    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    activateTabs() {
      this.tabs.forEach(tab => {
        tab.disabled = false;
      });
      this.showCancel = false;
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {
            // Do nothing, just close the modal
          }
        );
    },

    openClaim() {
      if (this.readonly) return;
      window.Ajax.post(`/incidents/${this.incident}/re-open`, {})
        .then(response => {
          let route = null;

          switch (response.data.stage.name) {
            case 'first_notice_of_loss':
              route = `/first-notice-of-loss/${response.data.identifier}`;
              break;

            case 'deployment':
              route = `/deployment/${response.data.identifier}`;
              break;

            case 'recovery':
              route = `/recovery/${response.data.identifier}`;
              break;

            default:
              route = `/qualification/${response.data.identifier}`;
              break;
          }

          this.route(route);

          window.toastify.success(
            'Success!',
            'The claim was re-opened successfully!'
          );
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an issue re-opening that claim.'
          );
        });
    },

    retrieveAndSetIncident() {
      this.$store.commit('Incident/clearRecord');

      window.Ajax.get(`/incidents/${this.incident}?filter[status]=rejected`)
        .then(response => {
          this.$store.dispatch('Incident/setIncident', response.data);

          this.loadingIncident = false;
        })
        .catch(error => {
          window.toastify.error('Sorry, there was a problem loading the claim');
        });
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
